import React, { useMemo } from 'react';
import { BaseTool, IToolManifest, IToolContext, IToolComponentProps } from '../../core/tools/interfaces/ITool';
import { ICommand } from '../../core/commands/ICommand';
import { UpdateTableGridCommand } from '../../core/commands/UpdateTableGridCommand';
import { Button } from '../../components/ui/button';
import { ArrowDown, ArrowUp, ArrowLeft, ArrowRight, Check } from 'lucide-react';
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '../../components/ui/dropdown-menu';
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../components/ui/tooltip';
import { useTableCellStore } from '../../stores/tableCellStore';
import { useDiagramStore } from '../../stores/diagramStore';
import { ModifyTableGridAtIndexCommand, ModifyTableGridAtIndexParams } from '../../core/commands/ModifyTableGridAtIndexCommand';
import { ToggleTableMergeCommand } from '../../core/commands/ToggleTableMergeCommand';
import type { TableCellKey } from '../../stores/tableCellStore';
import { useTranslation } from '@/hooks/useTranslation';

const clampCount = (value: unknown, fallback: number, min = 1) => {
  if (typeof value === 'number' && Number.isFinite(value)) {
    return Math.max(min, Math.floor(value));
  }
  return fallback;
};

type TableGridParams = { field: 'rowCount' | 'columnCount' | 'showHeader' | 'showHeaderColumn' | 'autoResizeCells'; value: number | boolean };
type TableMergeParams = { action: 'merge' | 'unmerge' };
type TableGridExecuteParams = TableGridParams | TableMergeParams | ModifyTableGridAtIndexParams;

type StructureAction =
  | { kind: 'insert'; axis: 'row' | 'column'; position: 'before' | 'after' }
  | { kind: 'remove'; axis: 'row' | 'column' };

type MergedRegion = { startRow: number; startCol: number; endRow: number; endCol: number };

const readShapeAttrs = (shape: unknown): Record<string, unknown> => {
  if (!shape || typeof shape !== 'object') return {};
  const data = (shape as { data?: unknown }).data;
  if (!data || typeof data !== 'object') return {};
  const attrs = (data as { attrs?: unknown }).attrs;
  return attrs && typeof attrs === 'object' && !Array.isArray(attrs) ? (attrs as Record<string, unknown>) : {};
};

const isMergedRegion = (value: unknown): value is MergedRegion => {
  if (!value || typeof value !== 'object') return false;
  const candidate = value as Partial<Record<keyof MergedRegion, unknown>>;
  return (
    typeof candidate.startRow === 'number' &&
    typeof candidate.startCol === 'number' &&
    typeof candidate.endRow === 'number' &&
    typeof candidate.endCol === 'number'
  );
};

const readMergedRegions = (value: unknown): MergedRegion[] => (Array.isArray(value) ? value.filter(isMergedRegion) : []);

const isTableMergeParams = (params: TableGridExecuteParams): params is TableMergeParams => 'action' in params;

const isTableGridParams = (params: TableGridExecuteParams): params is TableGridParams => 'field' in params;

const parseKey = (k: TableCellKey) => {
  const [rStr, cStr] = k.split(',');
  const r = Number(rStr);
  const c = Number(cStr);
  if (!Number.isFinite(r) || !Number.isFinite(c)) return null;
  return { r, c };
};

const overlaps = (a: MergedRegion, b: MergedRegion) => {
  const rowOverlap = a.startRow <= b.endRow && b.startRow <= a.endRow;
  const colOverlap = a.startCol <= b.endCol && b.startCol <= a.endCol;
  return rowOverlap && colOverlap;
};

const containsCell = (r: MergedRegion, row: number, col: number) => {
  return row >= r.startRow && row <= r.endRow && col >= r.startCol && col <= r.endCol;
};

const getBoundsFromKeys = (keys: TableCellKey[]) => {
  const uniq = Array.from(new Set(keys));
  const parsed = uniq.map((k) => ({ k, p: parseKey(k) })).filter((x) => !!x.p) as Array<{ k: TableCellKey; p: { r: number; c: number } }>;
  if (parsed.length === 0) return null;

  let rMin = parsed[0].p.r;
  let rMax = parsed[0].p.r;
  let cMin = parsed[0].p.c;
  let cMax = parsed[0].p.c;
  for (const it of parsed) {
    rMin = Math.min(rMin, it.p.r);
    rMax = Math.max(rMax, it.p.r);
    cMin = Math.min(cMin, it.p.c);
    cMax = Math.max(cMax, it.p.c);
  }

  return { parsed, rMin, rMax, cMin, cMax };
};

const isFullRectangle = (keys: TableCellKey[], rMin: number, rMax: number, cMin: number, cMax: number) => {
  const set = new Set(keys);
  const expected = (rMax - rMin + 1) * (cMax - cMin + 1);
  if (set.size !== expected) return false;
  for (let r = rMin; r <= rMax; r += 1) {
    for (let c = cMin; c <= cMax; c += 1) {
      if (!set.has(`${r},${c}` as TableCellKey)) return false;
    }
  }
  return true;
};

const TableGridToolComponent: React.FC<IToolComponentProps> = ({ context, onExecute }) => {
  const { t } = useTranslation('diagram');
  const selectedIds = useMemo(() => Array.from(context.selection), [context.selection]);
  const firstShape = selectedIds.length > 0 ? context.shapes.get(selectedIds[0]) : null;
  const attrs = readShapeAttrs(firstShape);
  const rowCount = clampCount(attrs.rowCount, 3);
  const columnCount = clampCount(attrs.columnCount, 3);
  const showHeader = attrs.showHeader !== false;
  const showHeaderColumn = attrs.showHeaderColumn === true;
  const disabled = selectedIds.length === 0;
  const selections = useTableCellStore((s) => s.selections);
  const firstSel = selectedIds.length > 0 ? selections[selectedIds[0]] : undefined;
  const anchorKey = firstSel?.anchorKey ?? firstSel?.selectedKeys?.[0];
  const hasAnchor = !!anchorKey;
  const isHeaderAnchor = showHeader && (anchorKey?.startsWith('0,') ?? false);

  const mergedRegions = readMergedRegions(attrs.mergedRegions);
  const selectedKeys = useMemo(() => (firstSel?.selectedKeys ?? []) as TableCellKey[], [firstSel?.selectedKeys]);

  const mergeState = useMemo(() => {
    if (selectedIds.length !== 1) return { canMerge: false, canUnmerge: false };
    if (selectedKeys.length === 0) return { canMerge: false, canUnmerge: false };

    const bounds = getBoundsFromKeys(selectedKeys);
    if (!bounds) return { canMerge: false, canUnmerge: false };
    const { rMin, rMax, parsed } = bounds;

    const anyInMerged = mergedRegions.some((mr) => parsed.some((p) => containsCell(mr, p.p.r, p.p.c)));
    if (anyInMerged) {
      return { canMerge: false, canUnmerge: true };
    }

    const mergeKeys = showHeader && rMin === 0 && rMax > 0
      ? selectedKeys.filter((k) => parseKey(k)?.r === 0)
      : selectedKeys;
    const mergeBounds = getBoundsFromKeys(mergeKeys);
    if (!mergeBounds) return { canMerge: false, canUnmerge: false };
    const { rMin: mrMin, rMax: mrMax, cMin: mcMin, cMax: mcMax } = mergeBounds;

    if (mergeKeys.length < 2) return { canMerge: false, canUnmerge: false };
    if (!isFullRectangle(mergeKeys, mrMin, mrMax, mcMin, mcMax)) return { canMerge: false, canUnmerge: false };

    const nextRegion: MergedRegion = { startRow: mrMin, startCol: mcMin, endRow: mrMax, endCol: mcMax };
    const hasOverlap = mergedRegions.some((r) => overlaps(r, nextRegion));
    if (hasOverlap) return { canMerge: false, canUnmerge: false };

    return { canMerge: true, canUnmerge: false };
  }, [mergedRegions, selectedIds.length, selectedKeys, showHeader]);

  const toggleMerge = () => {
    if (mergeState.canUnmerge) {
      onExecute?.({ action: 'unmerge' } satisfies TableMergeParams);
      return;
    }
    if (mergeState.canMerge) {
      onExecute?.({ action: 'merge' } satisfies TableMergeParams);
    }
  };

  const updateField = (field: TableGridParams['field'], nextValue: number | boolean) => {
    onExecute?.({ field, value: nextValue });
  };

  const toggleHeader = () => updateField('showHeader', !showHeader);
  const toggleHeaderColumn = () => updateField('showHeaderColumn', !showHeaderColumn);
  // autoResizeCells is controlled next to the Text (T) control in TableCellStyleTool

  const performStructureAction = (action: StructureAction) => {
    if (selectedIds.length === 0) return;
    const rowIndexByNodeId: Record<string, number> = {};
    const colIndexByNodeId: Record<string, number> = {};
    const sels = useTableCellStore.getState().selections;

    // Build per-node indices from anchor selection
    selectedIds.forEach((id) => {
      const s = sels[id];
      const key = s?.anchorKey ?? s?.selectedKeys?.[0];
      if (!key) return;
      const [rStr, cStr] = key.split(',');
      const r = Number(rStr);
      const c = Number(cStr);
      if (Number.isFinite(r)) rowIndexByNodeId[id] = r;
      if (Number.isFinite(c)) colIndexByNodeId[id] = c;
    });

    const payload: ModifyTableGridAtIndexParams = action.kind === 'insert'
      ? {
          axis: action.axis,
          op: 'insert',
          position: action.position,
          rowIndexByNodeId,
          colIndexByNodeId,
        }
      : {
          axis: action.axis,
          op: 'remove',
          rowIndexByNodeId,
          colIndexByNodeId,
        };

    onExecute?.(payload);
  };

  return (
    <TooltipProvider delayDuration={150}>
      <div className="flex items-stretch gap-1 px-1">
        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant={(showHeader || showHeaderColumn) ? 'secondary' : 'ghost'}
                    size="icon"
                    disabled={disabled}
                    aria-label={t('table.headerOptionsAria')}
                    className="h-8 w-8"
                    data-testid="tid.tools.tableGrid.header.trigger"
                  >
                    <img src="/assets/app-icons/table/headers.svg" alt={t('table.headersTooltip')} className="h-4 w-4 dark:invert" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start">
                  <DropdownMenuItem
                    disabled={disabled}
                    data-testid="tid.tools.tableGrid.header.row"
                    onSelect={(e) => {
                      e.preventDefault();
                      toggleHeader();
                    }}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-xs">{t('table.headerRow')}</span>
                    {showHeader ? <Check className="h-4 w-4" /> : <span className="h-4 w-4" />}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    disabled={disabled}
                    data-testid="tid.tools.tableGrid.header.column"
                    onSelect={(e) => {
                      e.preventDefault();
                      toggleHeaderColumn();
                    }}
                    className="flex items-center justify-between gap-2"
                  >
                    <span className="text-xs">{t('table.headerColumn')}</span>
                    {showHeaderColumn ? <Check className="h-4 w-4" /> : <span className="h-4 w-4" />}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} avoidCollisions={false}>{t('table.headersTooltip')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              disabled={disabled || selectedIds.length !== 1 || (!mergeState.canMerge && !mergeState.canUnmerge)}
              onClick={toggleMerge}
              aria-label={mergeState.canUnmerge ? t('table.unmergeCellsAria') : t('table.mergeCellsAria')}
              className="h-8 w-8"
              data-testid="tid.tools.tableGrid.merge.trigger"
            >
              <img
                src="/assets/app-icons/table/merge.svg"
                alt={mergeState.canUnmerge ? t('table.unmergeIconAlt') : t('table.mergeIconAlt')}
                className="h-4 w-4 dark:invert"
              />
            </Button>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} avoidCollisions={false}>
            {mergeState.canUnmerge ? t('table.unmergeCells') : t('table.mergeCells')}
          </TooltipContent>
        </Tooltip>

        {/* Separator between Merge and Insert */}
        <div className="w-[1px] bg-transparent border-r-[1px] border-solid border-[#E5E7EB] dark:border-[#3f3f46] self-stretch -my-1.5 mx-1" />

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={disabled || !hasAnchor}
                    aria-label={t('table.insertAria')}
                    data-testid="tid.tools.tableGrid.insert.trigger"
                  >
                    <img src="/assets/app-icons/table/insert.svg" alt={t('table.insertIconAlt')} className="h-4 w-4 dark:invert" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem
                    data-testid="tid.tools.tableGrid.insert.row-before"
                    onClick={() => performStructureAction({ kind: 'insert', axis: 'row', position: 'before' })}
                    disabled={disabled || !hasAnchor || isHeaderAnchor}
                  >
                    <ArrowUp className="h-4 w-4" /> {t('table.addRowAbove')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="tid.tools.tableGrid.insert.row-after"
                    onClick={() => performStructureAction({ kind: 'insert', axis: 'row', position: 'after' })}
                    disabled={disabled || !hasAnchor}
                  >
                    <ArrowDown className="h-4 w-4" /> {t('table.addRowBelow')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="tid.tools.tableGrid.insert.column-before"
                    onClick={() => performStructureAction({ kind: 'insert', axis: 'column', position: 'before' })}
                    disabled={disabled || !hasAnchor}
                  >
                    <ArrowLeft className="h-4 w-4" /> {t('table.addColumnLeft')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="tid.tools.tableGrid.insert.column-after"
                    onClick={() => performStructureAction({ kind: 'insert', axis: 'column', position: 'after' })}
                    disabled={disabled || !hasAnchor}
                  >
                    <ArrowRight className="h-4 w-4" /> {t('table.addColumnRight')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} avoidCollisions={false}>{t('table.insertRowColumnTooltip')}</TooltipContent>
        </Tooltip>

        <Tooltip>
          <TooltipTrigger asChild>
            <span className="inline-flex">
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="h-8 w-8"
                    disabled={disabled || !hasAnchor}
                    aria-label={t('table.removeAria')}
                    data-testid="tid.tools.tableGrid.remove.trigger"
                  >
                    <img src="/assets/app-icons/table/remove.svg" alt={t('table.removeIconAlt')} className="h-4 w-4 dark:invert" />
                  </Button>
                </DropdownMenuTrigger>
                <DropdownMenuContent align="start" className="w-48">
                  <DropdownMenuItem
                    data-testid="tid.tools.tableGrid.remove.row"
                    onClick={() => performStructureAction({ kind: 'remove', axis: 'row' })}
                    disabled={disabled || !hasAnchor || rowCount <= 1 || isHeaderAnchor}
                  >
                    <ArrowDown className="h-4 w-4" /> {t('table.removeSelectedRow')}
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    data-testid="tid.tools.tableGrid.remove.column"
                    onClick={() => performStructureAction({ kind: 'remove', axis: 'column' })}
                    disabled={disabled || !hasAnchor || columnCount <= 1}
                  >
                    <ArrowRight className="h-4 w-4" /> {t('table.removeSelectedColumn')}
                  </DropdownMenuItem>
                </DropdownMenuContent>
              </DropdownMenu>
            </span>
          </TooltipTrigger>
          <TooltipContent side="top" sideOffset={6} avoidCollisions={false}>{t('table.removeRowColumnTooltip')}</TooltipContent>
        </Tooltip>

        {/* Auto-resize control is rendered next to the Text (T) control in TableCellStyleTool */}
      </div>
    </TooltipProvider>
  );
};

export class TableGridTool extends BaseTool {
  manifest: IToolManifest = {
    id: 'table-grid',
    name: 'Table grid',
    icon: 'table',
    category: 'edit',
    description: 'Adjust table rows, columns, and header',
    version: '1.0.0',

    requiresSelection: true,
    minSelection: 1,

    ui: {
      toolbar: false,
      contextMenu: false,
      propertyPanel: false,
      sidebar: false,
      floatingPanel: true,
      statusBar: false,
    },

    applicability: {
      contextTypes: ['genericShape'],
    },

    capabilities: {
      modifiesShapes: true,
      createsShapes: false,
      deletesShapes: false,
      modifiesConnectors: false,
      createsConnectors: false,
      deletesConnectors: false,
      requiresUserInput: true,
      supportsMultiSelect: true,
      supportsBatchOperations: true,
    },
  };

  canExecute(context: IToolContext): boolean {
    if (!super.canExecute(context)) return false;
    if (context.selection.size === 0) return false;
    const store = useDiagramStore.getState();
    const selectedNodes = Array.from(context.selection).map((id) => store.nodes.find((n) => n.id === id)).filter(Boolean);
    return selectedNodes.length > 0 && selectedNodes.every((n) => n?.type === 'table');
  }

  execute(params?: TableGridExecuteParams): ICommand | null {
    if (!this.context || this.context.selection.size === 0 || !params) {
      return null;
    }
    const selectedIds = Array.from(this.context.selection);
    if (isTableMergeParams(params)) {
      const selectionState = useTableCellStore.getState().selections;
      const cellKeysByNodeId: Record<string, TableCellKey[]> = {};
      selectedIds.forEach((id) => {
        const keys = selectionState[id]?.selectedKeys ?? [];
        cellKeysByNodeId[id] = keys;
      });
      return new ToggleTableMergeCommand(selectedIds, {
        mode: params.action,
        cellKeysByNodeId,
      });
    }
    // Route to proper command based on params shape
    if (isTableGridParams(params)) {
      return new UpdateTableGridCommand(selectedIds, params);
    }
    return new ModifyTableGridAtIndexCommand(selectedIds, params as ModifyTableGridAtIndexParams);
  }

  getFloatingPanelComponent() {
    return TableGridToolComponent;
  }
}
